import { formatNumber } from "@/utils/formatting/format-number"

import type { ScatterPoint } from "../utils/buildScatterDataset"

// Recharts injects `active` and `payload` when this is passed as a Tooltip's
// `content`; for a scatter, each payload entry carries the data point.
interface ComparisonTooltipProps {
  active?: boolean
  payload?: { payload?: ScatterPoint }[]
  xLabel: string
  yLabel: string
  // Set when a bubble-size (z) metric is configured.
  zLabel?: string
  // Drill-down hint is only shown where clicking a point navigates.
  interactive: boolean
}

export function ComparisonTooltip({
  active,
  payload,
  xLabel,
  yLabel,
  zLabel,
  interactive,
}: ComparisonTooltipProps) {
  const point = active ? payload?.[0]?.payload : undefined
  if (!point) {
    return null
  }
  return (
    <div
      role="tooltip"
      className="rounded-lg border border-coval-border bg-surface-default p-3 shadow-lg"
    >
      {point.groupLabel && (
        <div className="mb-1.5 flex items-center gap-2 border-b border-coval-border pb-1.5">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: point.color }}
          />
          <span className="text-xs font-medium text-coval-text">
            {point.groupLabel}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-coval-text-muted">{xLabel}</span>
          <span className="text-xs font-medium text-coval-text">
            {point.xDisplay}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-coval-text-muted">{yLabel}</span>
          <span className="text-xs font-medium text-coval-text">
            {point.yDisplay}
          </span>
        </div>
        {zLabel && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-coval-text-muted">{zLabel}</span>
            <span className="text-xs font-medium text-coval-text">
              {point.z !== undefined ? formatNumber(point.z, 3) : "N/A"}
            </span>
          </div>
        )}
      </div>
      {interactive && (
        <div className="mt-2 border-t border-coval-border pt-1.5 text-xs italic text-coval-text-muted">
          Click to view simulation
        </div>
      )}
    </div>
  )
}
